import { useMutation } from '@tanstack/react-query';
import { useNavigate, useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';
import { authApi } from '../api/auth.api';
import { useAuthStore, Role } from '@/stores/authStore';
import { ROUTES } from '@/constants/routes';
import type { LoginRequest } from '@/types/api.types';

export function useLogin() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuthStore();

  const from = (location.state as { from?: { pathname: string } })?.from?.pathname || ROUTES.DASHBOARD;

  return useMutation({
    mutationFn: (data: LoginRequest) => authApi.login(data),
    onSuccess: (response) => {
      // Map API response to frontend format
      const { data } = response;
      const user = {
        id: data.user.id,
        email: data.user.email,
        name: data.user.name,
        phone: data.user.phone,
        role: data.user.role as Role,
        createdAt: data.user.created_at,
        is_super_admin: data.user.is_super_admin,
        must_change_password: data.user.must_change_password,
      };

      login(user, data.access_token, data.refresh_token);

      // Freshly-invited admins must set their own password before doing anything.
      if (user.must_change_password) {
        toast('Please set a new password to continue.', { icon: '🔒' });
        navigate(ROUTES.CHANGE_PASSWORD, { replace: true });
        return;
      }

      toast.success('Welcome back!');
      navigate(from, { replace: true });
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Invalid credentials');
    },
  });
}

export function useLogout() {
  const navigate = useNavigate();
  const { logout } = useAuthStore();

  return useMutation({
    mutationFn: () => authApi.logout(),
    onSuccess: () => {
      logout();
      toast.success('Logged out successfully');
      navigate(ROUTES.LOGIN);
    },
    onError: () => {
      // Still logout on error (token might be invalid)
      logout();
      navigate(ROUTES.LOGIN);
    },
  });
}

export function useForgotPassword() {
  return useMutation({
    mutationFn: (email: string) => authApi.forgotPassword(email),
    onSuccess: () => {
      toast.success('Password reset email sent. Check your inbox.');
    },
  });
}

export function useVerifyResetOtp() {
  return useMutation({
    mutationFn: ({ email, otp }: { email: string; otp: string }) =>
      authApi.verifyResetOtp(email, otp),
  });
}

export function useResetPassword() {
  const navigate = useNavigate();

  return useMutation({
    mutationFn: ({ token, password }: { token: string; password: string }) =>
      authApi.resetPassword(token, password),
    onSuccess: () => {
      toast.success('Password reset successfully. Please login.');
      navigate(ROUTES.LOGIN);
    },
  });
}
